import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from '../utils/apiHelper';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Fuel, Plus, Pencil, Trash2, FileText, CreditCard, Clock, ArrowRight, Search, BarChart3, TrendingUp } from "lucide-react";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { useTranslation } from "../contexts/I18nContext";

// Etichette parlanti per i tipi documento
const TIPO_DOC_LABELS = {
  FV: { label: "Fatt. Vendita", short: "FV", color: "bg-blue-50 text-blue-700 border-blue-200" },
  FA: { label: "Fatt. Acquisto", short: "FA", color: "bg-slate-50 text-slate-700 border-slate-200" },
  FVE: { label: "Fatt. Vendita Est.", short: "FVE", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  FAE: { label: "Fatt. Acquisto Est.", short: "FAE", color: "bg-zinc-50 text-zinc-700 border-zinc-200" },
  NCV: { label: "Nota Cred. Vendita", short: "NCV", color: "bg-orange-50 text-orange-700 border-orange-200" },
  NCA: { label: "Nota Cred. Acquisto", short: "NCA", color: "bg-amber-50 text-amber-700 border-amber-200" },
  NCVE: { label: "N.C. Vendita Est.", short: "NCVE", color: "bg-orange-50 text-orange-700 border-orange-200" },
  NCAE: { label: "N.C. Acquisto Est.", short: "NCAE", color: "bg-amber-50 text-amber-700 border-amber-200" },
};
const getTipoDocInfo = (t) => TIPO_DOC_LABELS[t] || { label: t || "-", short: t || "-", color: "bg-gray-50 text-gray-700 border-gray-200" };
const isNotaCreditoAcq = (t) => t === "NCA" || t === "NCAE";

export default function AutomezziCarburantiPage() {
  const { t } = useTranslation();
  const [rifornimenti, setRifornimenti] = useState([]);
  const [automezzi, setAutomezzi] = useState([]);
  const [allocazioniInAttesa, setAllocazioniInAttesa] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [movimenti, setMovimenti] = useState([]);
  const [movimentiCarta, setMovimentiCarta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [linkDialog, setLinkDialog] = useState({ open: false, item: null, type: null });
  const [collegaDialog, setCollegaDialog] = useState({ open: false, allocazione: null });
  const [collegaSearch, setCollegaSearch] = useState("");
  const [selectedRifornimenti, setSelectedRifornimenti] = useState([]);
  // Stato per il dialog di collegamento raggruppato: { [sotto_allocazione_id]: [rifornimento_id, ...] }
  const [selectedPerSotto, setSelectedPerSotto] = useState({});
  const [filterAllocazioni, setFilterAllocazioni] = useState("");
  const [filterRifornimenti, setFilterRifornimenti] = useState("");
  const [filterTipoDocAlloc, setFilterTipoDocAlloc] = useState("all");
  const [rifViewMode, setRifViewMode] = useState("all"); // 'all' | 'da-associare' | 'associati'
  const [onlySuggested, setOnlySuggested] = useState(false);
  const [activeTab, setActiveTab] = useState("rifornimenti");
  const [dashFrom, setDashFrom] = useState("");
  const [dashTo, setDashTo] = useState("");
  const [form, setForm] = useState({
    automezzo_id: "", data_rifornimento: "", litri: "", prezzo_litro: "", importo: "",
    tipo_carburante: "diesel", km_attuali: "", stazione: "", note: ""
  });

  const prefix = getApiPrefix();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rifRes, autoRes, allocRes, fattRes, movRes, movCartaRes] = await Promise.all([
        api.get(`${prefix}/automezzi/rifornimenti`).catch(() => ({ data: [] })),
        api.get(`${prefix}/automezzi`).catch(() => ({ data: [] })),
        api.get(`${prefix}/automezzi/allocazioni-carburanti`).catch(() => ({ data: [] })),
        api.get(`${prefix}/automezzi/contabilita/fatture`).catch(() => ({ data: [] })),
        api.get(`${prefix}/automezzi/contabilita/movimenti-bancari`).catch(() => ({ data: [] })),
        api.get(`${prefix}/card-movements?page_size=5000`).catch(() => ({ data: { items: [] } })),
      ]);
      setRifornimenti(rifRes.data);
      setAutomezzi(autoRes.data);
      setAllocazioniInAttesa(allocRes.data);
      setFatture(fattRes.data);
      setMovimenti(movRes.data);
      setMovimentiCarta(movCartaRes.data?.items || movCartaRes.data || []);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [prefix]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatCurrency = (v) => `\u20AC ${(parseFloat(v) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;

  // Converte una data ISO "YYYY-MM-DD" o "YYYY-MM-DDTHH:..." in "DD-MM-YYYY"
  const formatDate = (v) => {
    if (!v) return '-';
    const s = String(v).split('T')[0];
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : s;
  };

  const handleSave = async () => {
    if (!form.automezzo_id || !form.data_rifornimento || !form.importo) {
      handleApiError({ message: t("errors.requiredVehicleDateAmount") }); return;
    }
    // Converti stringhe vuote in null per i campi opzionali numerici
    const payload = {
      ...form,
      litri: form.litri === "" ? null : parseFloat(form.litri) || null,
      prezzo_litro: form.prezzo_litro === "" ? null : parseFloat(form.prezzo_litro) || null,
      importo: parseFloat(form.importo),
      km_attuali: form.km_attuali === "" ? null : parseInt(form.km_attuali) || null,
      stazione: form.stazione || null,
      note: form.note || null,
    };
    try {
      if (dialog.editing) {
        await api.put(`${prefix}/automezzi/rifornimenti/${dialog.editing}`, payload);
        showSuccess(t("success.refuelingUpdated"));
      } else {
        await api.post(`${prefix}/automezzi/rifornimenti`, payload);
        showSuccess(t("success.refuelingCreated"));
      }
      setDialog({ open: false, editing: null });
      fetchData();
    } catch (err) {
      
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        handleApiError({ message: detail.map(d => d.msg).join(', ') });
      } else {
        handleApiError({ message: detail || "Errore salvataggio" });
      }
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(t("confirm.deleteRefueling"))) return;
    try {
      await api.delete(`${prefix}/automezzi/rifornimenti/${id}`);
      showSuccess(t("success.refuelingDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const handleLinkFattura = async (fatturaId) => {
    if (!linkDialog.item) return;
    try {
      await api.post(`${prefix}/automezzi/rifornimenti/${linkDialog.item.id}/collega-fattura`, null, {
        params: { fattura_id: fatturaId === "_none" ? "" : fatturaId }
      });
      showSuccess(fatturaId && fatturaId !== "_none" ? "Fattura collegata" : "Collegamento rimosso");
      setLinkDialog({ open: false, item: null, type: null });
      fetchData();
    } catch (err) { handleApiError(err, t("errors.errorLink")); }
  };

  const handleLinkMovimento = async (movId) => {
    if (!linkDialog.item) return;
    try {
      await api.post(`${prefix}/automezzi/rifornimenti/${linkDialog.item.id}/collega-movimento`, null, {
        params: { movimento_bancario_id: movId || "" }
      });
      showSuccess(movId ? "Movimento collegato" : "Collegamento rimosso");
      setLinkDialog({ open: false, item: null, type: null });
      fetchData();
    } catch (err) { handleApiError(err, t("errors.errorLink")); }
  };

  const handleCollegaRifornimenti = async () => {
    if (!collegaDialog.allocazione || selectedRifornimenti.length === 0) {
      handleApiError({ message: t("errors.selectAtLeastOneRefueling") }); return;
    }
    
    // Validazione: totale scontrini non può superare totale fattura
    if (collegaDialog.allocazione.source_type === 'fattura') {
      const totaleSelezionato = selectedRifornimenti.reduce((sum, id) => 
        sum + (rifNonCollegati.find(r => r.id === id)?.importo || 0), 0);
      const totaleFattura = collegaDialog.allocazione.fattura_totale_display || Math.abs(collegaDialog.allocazione.fattura_totale);
      
      if (totaleSelezionato > totaleFattura + 0.01) { // tolleranza di 1 centesimo
        handleApiError({ message: `Il totale scontrini (${formatCurrency(totaleSelezionato)}) supera il totale fattura (${formatCurrency(totaleFattura)})` });
        return;
      }
    }
    
    try {
      await api.post(`${prefix}/automezzi/allocazioni-carburanti/${collegaDialog.allocazione.id}/collega`, {
        rifornimento_ids: selectedRifornimenti
      });
      showSuccess(t("success.refuelingLinked"));
      setCollegaDialog({ open: false, allocazione: null });
      setSelectedRifornimenti([]);
      fetchData();
    } catch (err) { handleApiError(err, t("errors.errorLink")); }
  };

  // Collega una singola sotto-allocazione (quota) ai rifornimenti selezionati.
  // Validazione: la somma scontrini non deve superare l'importo IVA-inclusivo della quota (per fatture).
  const handleCollegaSotto = async (sotto) => {
    const rifIds = selectedPerSotto[sotto.id] || [];
    if (rifIds.length === 0) {
      handleApiError({ message: "Seleziona almeno un rifornimento per questa quota" });
      return;
    }
    const totaleSelezionato = rifIds.reduce((sum, id) =>
      sum + (rifNonCollegati.find(r => r.id === id)?.importo || 0), 0);
    const quotaImporto = Math.abs(sotto.importo_ivato ?? sotto.importo ?? 0);
    if (collegaDialog.allocazione?.source_type === 'fattura' && totaleSelezionato > quotaImporto + 0.01) {
      handleApiError({ message: `Il totale scontrini (${formatCurrency(totaleSelezionato)}) supera l'importo della quota (${formatCurrency(quotaImporto)})` });
      return;
    }
    try {
      await api.post(`${prefix}/automezzi/allocazioni-carburanti/${sotto.id}/collega`, {
        rifornimento_ids: rifIds
      });
      showSuccess("Quota collegata");
      // Pulisce solo la selezione di questa quota; il dialog rimane aperto se ci sono altre quote
      setSelectedPerSotto(prev => {
        const out = { ...prev };
        delete out[sotto.id];
        return out;
      });
      // Refetch per aggiornare l'elenco delle sotto-allocazioni in attesa
      fetchData();
      // Se questa era l'ultima quota, il refetch chiuderà naturalmente il sorgente; chiudi anche il dialog.
      const remaining = (collegaDialog.allocazione?.sotto_allocazioni || []).filter(s => s.id !== sotto.id);
      if (remaining.length === 0) {
        setCollegaDialog({ open: false, allocazione: null });
      } else {
        // Aggiorna l'allocazione locale rimuovendo la sotto-allocazione appena collegata,
        // così l'utente vede sparire la quota senza chiudere il dialog
        setCollegaDialog(prev => ({
          ...prev,
          allocazione: prev.allocazione
            ? { ...prev.allocazione, sotto_allocazioni: remaining }
            : prev.allocazione
        }));
      }
    } catch (err) {
      handleApiError(err, "Errore collegamento quota");
    }
  };

  const openEdit = (r) => {
    setForm({
      automezzo_id: r.automezzo_id, data_rifornimento: r.data_rifornimento, litri: r.litri || "",
      prezzo_litro: r.prezzo_litro || "", importo: r.importo || "", tipo_carburante: r.tipo_carburante || "diesel",
      km_attuali: r.km_attuali || "", stazione: r.stazione || "", note: r.note || ""
    });
    setDialog({ open: true, editing: r.id });
  };

  const openNew = () => {
    setForm({ automezzo_id: "", data_rifornimento: "", litri: "", prezzo_litro: "", importo: "", tipo_carburante: "diesel", km_attuali: "", stazione: "", note: "" });
    setDialog({ open: true, editing: null });
  };

  const rifNonCollegati = rifornimenti.filter(r => !r.fattura_id && !r.movimento_bancario_id);

  // Helper: espande un valore numerico in tutte le variazioni utili per la ricerca
  // (con virgola/punto, con/senza decimali, con padding zeri)
  const expandNumeric = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const n = Number(val);
    if (isNaN(n)) return String(val);
    const variants = new Set();
    variants.add(String(val));
    variants.add(String(n));
    variants.add(String(n).replace('.', ','));
    variants.add(n.toFixed(2));
    variants.add(n.toFixed(2).replace('.', ','));
    variants.add(n.toFixed(1));
    variants.add(n.toFixed(1).replace('.', ','));
    variants.add(String(Math.floor(n)));
    return Array.from(variants).join(' ');
  };

  // Normalizza la query utente: lowercase + trim, lascia sia "," che "." perché entrambi sono nel haystack
  const normalizeQuery = (q) => String(q || '').trim().toLowerCase();

  // Filtro testuale + tipo documento su allocazioni in attesa (raggruppate per documento sorgente)
  const filterAllocText = normalizeQuery(filterAllocazioni);
  const allocazioniFiltered = allocazioniInAttesa.filter(a => {
    // Filtro tipo documento (solo per fatture, movimenti bancari/carta esclusi solo se filtro !== all)
    if (filterTipoDocAlloc !== "all") {
      if (a.source_type !== 'fattura' || a.tipo_documento !== filterTipoDocAlloc) return false;
    }
    if (filterAllocText === "") return true;
    const sottoText = (a.sotto_allocazioni || []).map(s => `${s.descrizione || ""} ${expandNumeric(s.importo)}`).join(" ");
    const haystack = [
      a.fattura_numero, a.fornitore,
      a.mov_descrizione,
      a.fattura_data, a.mov_data,
      expandNumeric(a.fattura_totale_display || a.fattura_totale || a.importo_totale || a.importo),
      expandNumeric(a.costo_bilancio_totale || a.costo_bilancio),
      sottoText
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(filterAllocText);
  });

  // Filtro testuale sui rifornimenti — supporta virgola/punto e ricerche parziali su numeri
  const filterRifText = normalizeQuery(filterRifornimenti);
  const rifornimentiFiltered = filterRifText === "" ? rifornimenti : rifornimenti.filter(r => {
    const haystack = [
      r.marca, r.modello, r.targa, r.data_rifornimento,
      r.tipo_carburante, r.stazione, r.note,
      expandNumeric(r.litri),
      expandNumeric(r.prezzo_litro),
      expandNumeric(r.importo),
      expandNumeric(r.costo_imponibile),
      expandNumeric(r.costo_effettivo),
      expandNumeric(r.km_attuali)
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(filterRifText);
  });

  // Split tra rifornimenti già collegati a fattura e quelli da associare
  const rifornimentiAssociati = rifornimentiFiltered.filter(r => r.fattura_id);
  const rifornimentiDaAssociare = rifornimentiFiltered.filter(r => !r.fattura_id);

  // === Auto-suggerimento collegamento Fattura-Rifornimento ===
  // Per ogni rifornimento "da associare" cerca la sotto-allocazione che potrebbe corrispondere
  // Criteri: importo identico (±0.10€) e data entro ±7 giorni. Score = qualità match.
  const suggestionsByRifornimento = useMemo(() => {
    const map = {};
    if (!allocazioniInAttesa.length || !rifornimenti.length) return map;
    // Costruisco lista flat di sotto-allocazioni candidate (con relativa allocazione parent)
    const candidate = [];
    for (const a of allocazioniInAttesa) {
      const subs = a.sotto_allocazioni || [];
      const refDate = a.fattura_data || a.mov_data;
      if (subs.length === 0) {
        // Allocazione senza sotto: usa importo_totale o importo
        candidate.push({ alloc: a, sotto: null, importo: Math.abs(a.importo_totale ?? a.importo ?? 0), data: refDate });
      } else {
        for (const s of subs) {
          candidate.push({ alloc: a, sotto: s, importo: Math.abs(s.importo ?? 0), data: refDate });
        }
      }
    }
    for (const r of rifornimenti) {
      if (r.fattura_id) continue; // già collegato
      const importoRif = Math.abs(r.importo || 0);
      const dataRif = r.data_rifornimento;
      let best = null;
      let bestScore = -1;
      for (const c of candidate) {
        if (importoRif <= 0 || c.importo <= 0) continue;
        const diff = Math.abs(importoRif - c.importo);
        if (diff > 0.10) continue;
        // Calcolo distanza in giorni se entrambe le date sono presenti
        let giorni = 999;
        if (dataRif && c.data) {
          const dRif = new Date(dataRif);
          const dAll = new Date(c.data);
          if (!isNaN(dRif) && !isNaN(dAll)) {
            giorni = Math.abs((dAll - dRif) / 86400000);
          }
        }
        if (giorni > 7) continue;
        // Score: peso importo (precisione) + vicinanza data
        const score = (1 - diff / 0.10) * 70 + (1 - giorni / 7) * 30;
        if (score > bestScore) {
          bestScore = score;
          best = c;
        }
      }
      if (best) {
        map[r.id] = { alloc: best.alloc, sotto: best.sotto, score: bestScore };
      }
    }
    return map;
  }, [rifornimenti, allocazioniInAttesa]);

  // Dashboard: calcolo media €/km su intervallo [dashFrom, dashTo]
  // Logica: per ogni veicolo, ordina i rifornimenti per km crescenti.
  // Per ogni rifornimento X con km > 0, calcola delta_km = km(X) - km(X-1).
  // Se delta_km > 0 e in [dashFrom, dashTo], calcola €/km = importo(X) / delta_km.
  // Media = somma(€/km) / N rifornimenti validi.
  const dashStats = useMemo(() => {
    const dataInizio = dashFrom || "0000-00-00";
    const dataFine = dashTo || "9999-12-31";
    // Raggruppa per automezzo
    const perVeicolo = {};
    for (const r of rifornimenti) {
      if (!r.automezzo_id || !r.km_attuali) continue;
      if (!perVeicolo[r.automezzo_id]) perVeicolo[r.automezzo_id] = [];
      perVeicolo[r.automezzo_id].push(r);
    }
    const valoriEuroKm = [];
    let totaleSpesa = 0;
    let totaleKm = 0;
    let nRifornimentiPeriodo = 0;
    let nRifornimentiTotali = 0;
    Object.values(perVeicolo).forEach(arr => {
      arr.sort((a, b) => (a.km_attuali || 0) - (b.km_attuali || 0));
      for (let i = 1; i < arr.length; i++) {
        const cur = arr[i];
        const prev = arr[i - 1];
        nRifornimentiTotali += 1;
        const deltaKm = (cur.km_attuali || 0) - (prev.km_attuali || 0);
        if (deltaKm <= 0) continue;
        const data = cur.data_rifornimento;
        if (!data || data < dataInizio || data > dataFine) continue;
        const importo = parseFloat(cur.importo) || 0;
        if (importo <= 0) continue;
        valoriEuroKm.push(importo / deltaKm);
        totaleSpesa += importo;
        totaleKm += deltaKm;
        nRifornimentiPeriodo += 1;
      }
    });
    const mediaPuntuale = valoriEuroKm.length > 0 ? (valoriEuroKm.reduce((a, b) => a + b, 0) / valoriEuroKm.length) : 0;
    const mediaPonderata = totaleKm > 0 ? (totaleSpesa / totaleKm) : 0;
    return { mediaPuntuale, mediaPonderata, totaleSpesa, totaleKm, nRifornimentiPeriodo, nRifornimentiTotali };
  }, [rifornimenti, dashFrom, dashTo]);

  // Statistiche €/km per singolo veicolo (nel periodo selezionato)
  const dashPerVeicolo = useMemo(() => {
    const dataInizio = dashFrom || "0000-00-00";
    const dataFine = dashTo || "9999-12-31";
    const perVeicolo = {};
    for (const r of rifornimenti) {
      if (!r.automezzo_id || !r.km_attuali) continue;
      if (!perVeicolo[r.automezzo_id]) perVeicolo[r.automezzo_id] = [];
      perVeicolo[r.automezzo_id].push(r);
    }
    const out = [];
    Object.entries(perVeicolo).forEach(([autoId, arr]) => {
      arr.sort((a, b) => (a.km_attuali || 0) - (b.km_attuali || 0));
      const valori = [];
      let totSpesa = 0;
      let totKm = 0;
      let n = 0;
      for (let i = 1; i < arr.length; i++) {
        const deltaKm = (arr[i].km_attuali || 0) - (arr[i - 1].km_attuali || 0);
        const data = arr[i].data_rifornimento;
        if (deltaKm <= 0 || !data || data < dataInizio || data > dataFine) continue;
        const importo = parseFloat(arr[i].importo) || 0;
        if (importo <= 0) continue;
        valori.push(importo / deltaKm);
        totSpesa += importo;
        totKm += deltaKm;
        n += 1;
      }
      if (n === 0) return;
      const automezzo = automezzi.find(a => a.id === autoId);
      out.push({
        automezzo_id: autoId,
        veicolo: automezzo ? `${automezzo.marca} ${automezzo.modello} (${automezzo.targa})` : autoId,
        nRifornimenti: n,
        mediaPuntuale: valori.reduce((a, b) => a + b, 0) / valori.length,
        mediaPonderata: totSpesa / totKm,
        totSpesa,
        totKm
      });
    });
    out.sort((a, b) => b.totSpesa - a.totSpesa);
    return out;
  }, [rifornimenti, automezzi, dashFrom, dashTo]);

  // Helper: classe colore badge in base allo score di match (0-100)
  const scoreColorClasses = (score) => {
    if (score >= 90) return 'text-emerald-700 border-emerald-400 bg-emerald-50 hover:bg-emerald-100';
    if (score >= 70) return 'text-amber-700 border-amber-400 bg-amber-50 hover:bg-amber-100';
    if (score >= 50) return 'text-orange-700 border-orange-400 bg-orange-50 hover:bg-orange-100';
    return 'text-rose-700 border-rose-400 bg-rose-50 hover:bg-rose-100';
  };

  // Set di fattura_id che hanno almeno un rifornimento suggerito (per filtro "solo match")
  const suggestedFatturaIds = useMemo(() => {
    const ids = new Set();
    Object.values(suggestionsByRifornimento).forEach(s => {
      if (s?.alloc?.fattura_id) ids.add(s.alloc.fattura_id);
    });
    return ids;
  }, [suggestionsByRifornimento]);

  // Helper: renderizza tabella rifornimenti (riutilizzabile tra Associati e Da Associare)
  const renderRifornimentiTable = (list) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="py-2 px-2">{t('common.vehicle')}</th>
            <th className="py-2 px-2">{t("common.date")}</th>
            <th className="py-2 px-2">{t("fuel.liters")}</th>
            <th className="py-2 px-2">{t("fuel.pricePerLiter")}</th>
            <th className="py-2 px-2">{t("fuel.receipt")}</th>
            <th className="py-2 px-2">{t('invoices.taxableAmount')}</th>
            <th className="py-2 px-2">{t('common.finalCost')}</th>
            <th className="py-2 px-2">{t("common.type")}</th>
            <th className="py-2 px-2">{t("fuel.station")}</th>
            <th className="py-2 px-2">KM</th>
            <th className="py-2 px-2">{t("fuel.accountingLink")}</th>
            <th className="py-2 px-2">{t("common.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {list.map(r => {
            const suggestion = !r.fattura_id ? suggestionsByRifornimento[r.id] : null;
            return (
            <tr key={r.id} className="border-b hover:bg-muted/50">
              <td className="py-2 px-2">{r.marca} {r.modello}<br/><span className="text-xs text-muted-foreground">{r.targa}</span></td>
              <td className="py-2 px-2">{formatDate(r.data_rifornimento)}</td>
              <td className="py-2 px-2">{r.litri ? `${r.litri} L` : '-'}</td>
              <td className="py-2 px-2">{r.prezzo_litro ? `${r.prezzo_litro} €/L` : '-'}</td>
              <td className="py-2 px-2 font-medium">{formatCurrency(r.importo)}</td>
              <td className="py-2 px-2 font-medium text-blue-600">{r.costo_imponibile ? formatCurrency(Math.abs(r.costo_imponibile)) : '-'}</td>
              <td className="py-2 px-2 font-medium text-purple-600">{r.costo_effettivo ? formatCurrency(Math.abs(r.costo_effettivo)) : '-'}</td>
              <td className="py-2 px-2"><Badge variant="outline" className="text-xs">{r.tipo_carburante}</Badge></td>
              <td className="py-2 px-2 text-xs">{r.stazione || '-'}</td>
              <td className="py-2 px-2">{r.km_attuali || '-'}</td>
              <td className="py-2 px-2">
                <div className="flex gap-1 items-center flex-wrap">
                  {r.fattura_id ? (
                    <Badge variant="outline" className="text-xs cursor-pointer text-green-600 border-green-300" onClick={() => setLinkDialog({ open: true, item: r, type: 'fattura' })}>
                      <FileText className="h-3 w-3 mr-1" />Fatt.
                    </Badge>
                  ) : suggestion ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className={`h-6 px-2 text-xs ${scoreColorClasses(suggestion.score)}`}
                      onClick={() => setLinkDialog({ open: true, item: r, type: 'fattura' })}
                      title={`Possibile match (score ${Math.round(suggestion.score)}/100): ${suggestion.alloc.fattura_numero} - ${suggestion.alloc.fornitore || 'N/D'}`}
                      data-testid={`suggest-fatt-${r.id}`}
                    >
                      <FileText className="h-3 w-3 mr-1" />? {suggestion.alloc.fattura_numero?.slice(-8) || '?'}
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground" onClick={() => setLinkDialog({ open: true, item: r, type: 'fattura' })}>
                      <FileText className="h-3 w-3 mr-1" />+Fatt.
                    </Button>
                  )}
                  {r.movimento_bancario_id ? (
                    <Badge variant="outline" className="text-xs cursor-pointer text-blue-600 border-blue-300" onClick={() => setLinkDialog({ open: true, item: r, type: 'movimento' })}>
                      <CreditCard className="h-3 w-3 mr-1" />Mov. Banca
                    </Badge>
                  ) : r.movimento_carta_id ? (
                    <Badge variant="outline" className="text-xs cursor-pointer text-purple-600 border-purple-300" onClick={() => setLinkDialog({ open: true, item: r, type: 'movimento' })}>
                      <CreditCard className="h-3 w-3 mr-1" />Mov. Carta
                    </Badge>
                  ) : (
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground" onClick={() => setLinkDialog({ open: true, item: r, type: 'movimento' })}>
                      <CreditCard className="h-3 w-3 mr-1" />+Mov.
                    </Button>
                  )}
                </div>
              </td>
              <td className="py-2 px-2">
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(r.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </td>
            </tr>
          );})}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4 p-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">{t("nav.fuel")}</h1>
          <p className="text-muted-foreground text-sm">Gestisci i rifornimenti carburante e visualizza le statistiche</p>
        </div>
        {activeTab === "rifornimenti" && (
          <Button onClick={openNew} data-testid="btn-nuovo-rifornimento"><Plus className="h-4 w-4 mr-2" /> Nuovo Rifornimento</Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="rifornimenti" data-testid="tab-rifornimenti"><Fuel className="h-4 w-4 mr-1.5" /> Rifornimenti</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><BarChart3 className="h-4 w-4 mr-1.5" /> Dashboard €/km</TabsTrigger>
        </TabsList>

        <TabsContent value="rifornimenti" className="space-y-4 mt-4">

      {/* Allocazioni in Attesa */}
      {allocazioniInAttesa.length > 0 && (
        <Card className="border-purple-200 bg-purple-50/50">
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-purple-600" />
              Allocazioni in Attesa di Collegamento ({allocazioniFiltered.length}{filterAllocText ? ` / ${allocazioniInAttesa.length}` : ""})
            </CardTitle>
            <CardDescription>Fatture e movimenti allocati come &quot;Carburante&quot; da collegare ai rifornimenti</CardDescription>
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca per fornitore, numero fattura, descrizione, importo..."
                  value={filterAllocazioni}
                  onChange={e => setFilterAllocazioni(e.target.value)}
                  className="pl-9 bg-white"
                  data-testid="filter-allocazioni"
                />
              </div>
              <Select value={filterTipoDocAlloc} onValueChange={setFilterTipoDocAlloc}>
                <SelectTrigger className="w-full sm:w-[220px] bg-white" data-testid="filter-tipo-documento-carb">
                  <SelectValue placeholder="Tipo documento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  <SelectItem value="FV">Fatt. Vendita (FV)</SelectItem>
                  <SelectItem value="FA">Fatt. Acquisto (FA)</SelectItem>
                  <SelectItem value="FVE">Fatt. Vendita Est. (FVE)</SelectItem>
                  <SelectItem value="FAE">Fatt. Acquisto Est. (FAE)</SelectItem>
                  <SelectItem value="NCV">Nota Cred. Vendita (NCV)</SelectItem>
                  <SelectItem value="NCA">Nota Cred. Acquisto (NCA)</SelectItem>
                  <SelectItem value="NCVE">N.C. Vendita Est. (NCVE)</SelectItem>
                  <SelectItem value="NCAE">N.C. Acquisto Est. (NCAE)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(() => {
              const allocList = onlySuggested
                ? allocazioniFiltered.filter(a => a.fattura_id && suggestedFatturaIds.has(a.fattura_id))
                : allocazioniFiltered;
              if (allocList.length === 0) {
                return <p className="text-sm text-muted-foreground py-3 text-center">{onlySuggested ? 'Nessuna allocazione con possibile match' : 'Nessuna allocazione corrisponde al filtro'}</p>;
              }
              return allocList.map((a, idx) => {
              const nSotto = (a.sotto_allocazioni || []).length;
              const hasMatch = a.fattura_id && suggestedFatturaIds.has(a.fattura_id);
              return (
              <div key={`${a.source_type || 'x'}-${a.source_id || a.id || 'noid'}-${idx}`} className={`flex items-center justify-between p-3 bg-white rounded border ${hasMatch ? 'border-amber-400 ring-1 ring-amber-200' : ''}`} data-testid={`alloc-row-${a.source_id || a.id}`}>
                <div className="flex items-center gap-4 flex-wrap">
                  <Badge variant="outline" className={a.source_type === 'fattura' ? 'text-green-600' : 'text-blue-600'}>
                    {a.source_type === 'fattura' ? <FileText className="h-3 w-3 mr-1" /> : <CreditCard className="h-3 w-3 mr-1" />}
                    {a.source_type === 'fattura' ? `Fatt. ${a.fattura_numero}` : a.mov_descrizione || 'Movimento'}
                  </Badge>
                  {a.source_type === 'fattura' && a.tipo_documento && (
                    <Badge variant="outline" className={`text-xs ${getTipoDocInfo(a.tipo_documento).color}`}>
                      {getTipoDocInfo(a.tipo_documento).label}
                    </Badge>
                  )}
                  {a.invoice_id_riferimento && a.fattura_riferimento_numero && (
                    <span className="text-xs text-amber-700">↳ Rif. Fatt. {a.fattura_riferimento_numero}</span>
                  )}
                  <span className="text-xs text-muted-foreground">{formatDate(a.fattura_data || a.mov_data)}</span>
                  {a.source_type === 'fattura' && (
                    <>
                      <span className="text-xs text-muted-foreground">{a.fornitore}</span>
                      <div className="flex gap-4 text-sm">
                        <div>
                          <span className="text-xs text-muted-foreground">Tot. Fattura:</span>
                          <span className="ml-1 font-medium">{formatCurrency(a.fattura_totale_display || Math.abs(a.fattura_totale))}</span>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Costo Bilancio (in attesa):</span>
                          <span className="ml-1 font-medium text-purple-600">{formatCurrency(a.costo_bilancio_totale ?? a.costo_bilancio)}</span>
                        </div>
                      </div>
                    </>
                  )}
                  {a.source_type !== 'fattura' && (
                    <span className="text-sm font-medium">{formatCurrency(Math.abs(a.importo_totale ?? a.importo ?? 0))}</span>
                  )}
                  {nSotto > 1 && (
                    <Badge variant="secondary" className="text-xs" data-testid={`badge-suballoc-${a.source_id || a.id}`}>
                      {nSotto} quote da collegare
                    </Badge>
                  )}
                </div>
                <Button size="sm" onClick={() => { 
                  setCollegaDialog({ open: true, allocazione: a }); 
                  setSelectedPerSotto({}); 
                  setSelectedRifornimenti([]); 
                  setCollegaSearch("");
                }} data-testid={`collega-btn-${a.source_id || a.id}`}>
                  <ArrowRight className="h-4 w-4 mr-1" />{t('common.link')}</Button>
              </div>
              );
            });
            })()}
          </CardContent>
        </Card>
      )}

      {/* Card Rifornimenti — vista filtrabile con switch */}
      {(() => {
        // Calcolo lista da mostrare in base a vista + filtro match
        let listaBase;
        if (rifViewMode === 'da-associare') listaBase = rifornimentiDaAssociare;
        else if (rifViewMode === 'associati') listaBase = rifornimentiAssociati;
        else listaBase = rifornimentiFiltered;
        const lista = onlySuggested
          ? listaBase.filter(r => !r.fattura_id && suggestionsByRifornimento[r.id])
          : listaBase;
        const totDaAssociare = rifornimenti.filter(r => !r.fattura_id).length;
        const totAssociati = rifornimenti.filter(r => r.fattura_id).length;
        const totSuggested = Object.keys(suggestionsByRifornimento).length;

        const viewTitle = rifViewMode === 'da-associare'
          ? t("fuel.toAssociateRefuelings")
          : rifViewMode === 'associati'
          ? t("fuel.associatedRefuelings")
          : t("fuel.title");
        const viewSubtitle = rifViewMode === 'da-associare'
          ? t("fuel.toAssociateSubtitle")
          : rifViewMode === 'associati'
          ? t("fuel.associatedSubtitle")
          : null;

        return (
          <Card data-testid="card-rifornimenti">
            <CardHeader className="py-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className={`h-4 w-4 ${rifViewMode === 'associati' ? 'text-green-600' : rifViewMode === 'da-associare' ? 'text-amber-600' : 'text-slate-600'}`} />
                    {viewTitle} ({lista.length}{(filterRifText || onlySuggested) ? ` / ${rifornimenti.length}` : ""})
                  </CardTitle>
                  {viewSubtitle && <p className="text-xs text-muted-foreground mt-1">{viewSubtitle}</p>}
                </div>
                <div className="flex items-center gap-1 flex-wrap" data-testid="rif-view-switch">
                  <Button
                    variant={rifViewMode === 'all' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setRifViewMode('all')}
                    data-testid="view-all-btn"
                  >
                    {t('common.all')} ({rifornimenti.length})
                  </Button>
                  <Button
                    variant={rifViewMode === 'da-associare' ? 'default' : 'outline'}
                    size="sm"
                    className={`h-7 text-xs ${rifViewMode === 'da-associare' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                    onClick={() => setRifViewMode('da-associare')}
                    data-testid="view-da-associare-btn"
                  >
                    {t('fuel.toAssociateRefuelings')} ({totDaAssociare})
                  </Button>
                  <Button
                    variant={rifViewMode === 'associati' ? 'default' : 'outline'}
                    size="sm"
                    className={`h-7 text-xs ${rifViewMode === 'associati' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                    onClick={() => setRifViewMode('associati')}
                    data-testid="view-associati-btn"
                  >
                    {t('fuel.associatedRefuelings')} ({totAssociati})
                  </Button>
                  <Button
                    variant={onlySuggested ? 'default' : 'outline'}
                    size="sm"
                    className={`h-7 text-xs ${onlySuggested ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'text-amber-700 border-amber-400'}`}
                    onClick={() => setOnlySuggested(v => !v)}
                    disabled={totSuggested === 0}
                    title={totSuggested === 0 ? 'Nessun match individuato' : `Filtra ${totSuggested} rifornimenti con possibile match`}
                    data-testid="view-match-btn"
                  >
                    💡 Solo match ({totSuggested})
                  </Button>
                </div>
              </div>
              <div className="relative mt-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("fuel.searchPlaceholder")}
                  value={filterRifornimenti}
                  onChange={e => setFilterRifornimenti(e.target.value)}
                  className="pl-9"
                  data-testid="filter-rifornimenti"
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-muted-foreground">{t("common.loading")}</p>
              ) : lista.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  {rifornimenti.length === 0
                    ? t("fuel.noRefuelingRegistered")
                    : onlySuggested
                    ? 'Nessun rifornimento con possibile match'
                    : rifViewMode === 'da-associare'
                    ? t("fuel.noToAssociate")
                    : rifViewMode === 'associati'
                    ? t("fuel.noAssociated")
                    : t("fuel.noMatchFilter")}
                </p>
              ) : (
                renderRifornimentiTable(lista)
              )}
            </CardContent>
          </Card>
        );
      })()}

        </TabsContent>

        <TabsContent value="dashboard" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                Statistiche €/km
              </CardTitle>
              <CardDescription>
                Media calcolata come <code>importo_scontrino / (km rifornimento corrente – km rifornimento precedente)</code> per ogni rifornimento del veicolo. Filtra l&apos;intervallo temporale per analizzare un periodo specifico.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="dash-from">{t('common.fromDate')}</Label>
                  <Input id="dash-from" type="date" value={dashFrom} onChange={e => setDashFrom(e.target.value)} data-testid="dash-from" />
                </div>
                <div>
                  <Label htmlFor="dash-to">{t('common.toDate')}</Label>
                  <Input id="dash-to" type="date" value={dashTo} onChange={e => setDashTo(e.target.value)} data-testid="dash-to" />
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={() => { setDashFrom(""); setDashTo(""); }} data-testid="dash-reset">
                    Azzera filtri
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Card className="border-emerald-200 bg-emerald-50/50">
                  <CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Media €/km (puntuale)</p>
                    <p className="text-2xl font-bold text-emerald-700" data-testid="stat-media-puntuale">
                      €{dashStats.mediaPuntuale.toFixed(4)}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">Media aritmetica dei singoli €/km</p>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Media €/km (ponderata)</p>
                    <p className="text-2xl font-bold text-blue-700" data-testid="stat-media-ponderata">
                      €{dashStats.mediaPonderata.toFixed(4)}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">Spesa totale / Km totali</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Spesa totale periodo</p>
                    <p className="text-2xl font-bold" data-testid="stat-spesa-totale">
                      {formatCurrency(dashStats.totaleSpesa)}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">{dashStats.nRifornimentiPeriodo} rifornimenti</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Km percorsi</p>
                    <p className="text-2xl font-bold" data-testid="stat-km-totali">
                      {dashStats.totaleKm.toLocaleString('it-IT')} km
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">Calcolati dai delta KM</p>
                  </CardContent>
                </Card>
              </div>

              {dashStats.nRifornimentiPeriodo === 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  Nessun rifornimento valido nel periodo. Per il calcolo servono almeno 2 rifornimenti consecutivi dello stesso veicolo con <strong>KM Attuali</strong> compilati.
                </div>
              )}

              {/* Tabella per veicolo */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Dettaglio per veicolo</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashPerVeicolo.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">Nessun dato disponibile per il periodo selezionato.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="py-2 px-2">{t('common.vehicle')}</th>
                            <th className="py-2 px-2 text-right">N° Rifornimenti</th>
                            <th className="py-2 px-2 text-right">Km percorsi</th>
                            <th className="py-2 px-2 text-right">Spesa totale</th>
                            <th className="py-2 px-2 text-right">Media €/km (puntuale)</th>
                            <th className="py-2 px-2 text-right">Media €/km (ponderata)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashPerVeicolo.map(v => (
                            <tr key={v.automezzo_id} className="border-b hover:bg-muted/50" data-testid={`dash-veicolo-${v.automezzo_id}`}>
                              <td className="py-2 px-2 font-medium">{v.veicolo}</td>
                              <td className="py-2 px-2 text-right">{v.nRifornimenti}</td>
                              <td className="py-2 px-2 text-right font-mono">{v.totKm.toLocaleString('it-IT')}</td>
                              <td className="py-2 px-2 text-right font-mono">{formatCurrency(v.totSpesa)}</td>
                              <td className="py-2 px-2 text-right font-mono text-emerald-700 font-semibold">€{v.mediaPuntuale.toFixed(4)}</td>
                              <td className="py-2 px-2 text-right font-mono text-blue-700 font-semibold">€{v.mediaPonderata.toFixed(4)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Nuovo/Modifica Rifornimento */}
      <Dialog open={dialog.open} onOpenChange={(open) => setDialog({ ...dialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog.editing ? 'Modifica Rifornimento' : 'Nuovo Rifornimento'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>{t('leasing.vehicle')} *</Label>
              <SearchableSelect
                options={automezzi.map(a => ({ value: a.id, label: `${a.marca} ${a.modello} (${a.targa})` }))}
                value={form.automezzo_id}
                onValueChange={(v) => setForm({ ...form, automezzo_id: v })}
                placeholder={t("dialogs.selectVehicle")}
                searchPlaceholder="Cerca veicolo..."
                testId="select-veicolo"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data Rifornimento *</Label><Input type="date" value={form.data_rifornimento} onChange={e => setForm({ ...form, data_rifornimento: e.target.value })} /></div>
              <div><Label>{t('common.amount')} *</Label><Input type="number" step="0.01" value={form.importo} onChange={e => setForm({ ...form, importo: e.target.value })} placeholder="0.00" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Litri</Label><Input type="number" step="0.01" value={form.litri} onChange={e => setForm({ ...form, litri: e.target.value })} /></div>
              <div><Label>Prezzo/L</Label><Input type="number" step="0.001" value={form.prezzo_litro} onChange={e => setForm({ ...form, prezzo_litro: e.target.value })} /></div>
              <div>
                <Label>Tipo Carburante</Label>
                <Select value={form.tipo_carburante} onValueChange={v => setForm({ ...form, tipo_carburante: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="diesel">Diesel</SelectItem>
                    <SelectItem value="benzina">Benzina</SelectItem>
                    <SelectItem value="gpl">GPL</SelectItem>
                    <SelectItem value="metano">Metano</SelectItem>
                    <SelectItem value="elettrico">Elettrico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>KM Attuali</Label><Input type="number" value={form.km_attuali} onChange={e => setForm({ ...form, km_attuali: e.target.value })} /></div>
              <div><Label>Stazione</Label><Input value={form.stazione} onChange={e => setForm({ ...form, stazione: e.target.value })} /></div>
            </div>
            <div><Label>{t("common.notes")}</Label><Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, editing: null })}>{t("common.cancel")}</Button>
            <Button onClick={handleSave}>{dialog.editing ? t("common.save") : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Collegamento Contabilita */}
      <Dialog open={linkDialog.open} onOpenChange={(open) => setLinkDialog({ ...linkDialog, open })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{linkDialog.type === 'fattura' ? t("maintenance.linkInvoice") : t("maintenance.linkBankMovement")}</DialogTitle>
            <DialogDescription>
              {linkDialog.item && `Rifornimento ${formatDate(linkDialog.item.data_rifornimento)} - ${linkDialog.item.targa}`}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {linkDialog.type === 'fattura' && (
              <div className="space-y-2">
                {linkDialog.item?.fattura_id ? (
                  <>
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-3">
                      <p className="text-sm font-medium text-green-800 mb-2">Fattura collegata:</p>
                      {(() => {
                        const f = fatture.find(fa => fa.id === linkDialog.item.fattura_id);
                        return f ? (
                          <div className="text-sm text-green-700 space-y-1">
                            <p><span className="font-medium">Numero:</span> {f.numero}</p>
                            <p><span className="font-medium">Data:</span> {formatDate(f.data)}</p>
                            <p><span className="font-medium">Fornitore:</span> {f.fornitore || 'N/D'}</p>
                            <p className="font-medium">Importo: {formatCurrency(f.totale)}</p>
                          </div>
                        ) : <p className="text-sm text-green-700">{t('dialogs.linkInvoice')}</p>;
                      })()}
                    </div>
                    <Button variant="outline" className="w-full text-red-600" onClick={() => handleLinkFattura("_none")}>{t('common.removeLink')}</Button>
                  </>
                ) : (
                  <>
                    {(() => {
                      const suggestion = linkDialog.item ? suggestionsByRifornimento[linkDialog.item.id] : null;
                      if (!suggestion) return null;
                      const a = suggestion.alloc;
                      const importoSugg = suggestion.sotto ? suggestion.sotto.importo : (a.importo_totale ?? a.importo);
                      return (
                        <div className="p-3 mb-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="suggestion-banner">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 text-sm text-amber-900">
                              <p className="font-semibold mb-1">💡 Possibile fattura corrispondente</p>
                              <p><span className="font-medium">{t('invoices.invoiceNumber')}:</span> {a.fattura_numero || '-'}</p>
                              <p><span className="font-medium">{t('common.date')}:</span> {formatDate(a.fattura_data)}</p>
                              <p><span className="font-medium">{t('common.supplier')}:</span> {a.fornitore || 'N/D'}</p>
                              <p className="font-medium">{t('common.amount')}: {formatCurrency(Math.abs(importoSugg || 0))} <span className="text-xs text-amber-700">(rifornimento: {formatCurrency(Math.abs(linkDialog.item?.importo || 0))})</span></p>
                              <p className="text-xs mt-1 text-amber-700">Match score: {Math.round(suggestion.score)}/100</p>
                            </div>
                            <Button
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700 shrink-0"
                              onClick={() => handleLinkFattura(a.fattura_id)}
                              data-testid="suggestion-accept-btn"
                            >
                              <ArrowRight className="h-4 w-4 mr-1" /> {t('common.link')}
                            </Button>
                          </div>
                        </div>
                      );
                    })()}
                    <SearchableSelect
                      options={fatture.map(f => ({ value: f.id, label: `${f.numero} - ${f.fornitore || 'N/D'} (${formatCurrency(f.totale)})` }))}
                      onValueChange={handleLinkFattura}
                      placeholder={t("dialogs.selectInvoice")}
                      searchPlaceholder="Cerca fattura..."
                    />
                  </>
                )}
              </div>
            )}
            {linkDialog.type === 'movimento' && (
              <div className="space-y-2">
                {linkDialog.item?.movimento_bancario_id ? (
                  <>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                      <p className="text-sm font-medium text-blue-800 mb-2">Movimento bancario collegato:</p>
                      {(() => {
                        const m = movimenti.find(mo => mo.id === linkDialog.item.movimento_bancario_id);
                        return m ? (
                          <div className="text-sm text-blue-700 space-y-1">
                            <p><span className="font-medium">Descrizione:</span> {m.descrizione || 'N/D'}</p>
                            <p><span className="font-medium">Data:</span> {formatDate(m.data)}{m.data_valuta ? ` | Valuta: ${formatDate(m.data_valuta)}` : ''}</p>
                            <p><span className="font-medium">Banca:</span> {m.banca_nome || 'N/D'}</p>
                            <p className={`font-medium ${m.tipo === 'entrata' ? 'text-green-600' : 'text-red-600'}`}>
                              Importo: {formatCurrency(Math.abs(m.importo))}
                            </p>
                          </div>
                        ) : <p className="text-sm text-blue-700">{t('common.linkedMovement')}</p>;
                      })()}
                    </div>
                    <Button variant="outline" className="w-full text-red-600" onClick={() => handleLinkMovimento(null)}>{t('common.removeLink')}</Button>
                  </>
                ) : linkDialog.item?.movimento_carta_id ? (
                  <>
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg mb-3">
                      <p className="text-sm font-medium text-purple-800 mb-2">Movimento carta collegato:</p>
                      {(() => {
                        const m = movimentiCarta.find(mo => mo.id === linkDialog.item.movimento_carta_id);
                        return m ? (
                          <div className="text-sm text-purple-700 space-y-1">
                            <p><span className="font-medium">Descrizione:</span> {m.descrizione || 'N/D'}</p>
                            <p><span className="font-medium">Data:</span> {formatDate(m.data || m.data_operazione)}</p>
                            <p><span className="font-medium">Carta:</span> {m.carta_nome || m.intestatario || 'N/D'}</p>
                            <p className="font-medium text-red-600">
                              Importo: {formatCurrency(Math.abs(m.importo))}
                            </p>
                          </div>
                        ) : <p className="text-sm text-purple-700">Movimento carta collegato</p>;
                      })()}
                    </div>
                    <Button variant="outline" className="w-full text-red-600" onClick={() => handleLinkMovimento(null)}>{t('common.removeLink')}</Button>
                  </>
                ) : (
                  movimenti.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Nessun movimento disponibile</p>
                  ) : (
                    movimenti.map(m => (
                      <Button key={m.id} variant="outline" className="w-full justify-between" onClick={() => handleLinkMovimento(m.id)}>
                        <span className="truncate max-w-xs">{m.descrizione || 'N/D'}</span>
                        <span className={m.tipo === 'entrata' ? 'text-green-600' : 'text-red-600'}>
                          {formatDate(m.data)} &bull; {formatCurrency(Math.abs(m.importo))}
                        </span>
                      </Button>
                    ))
                  )
                )}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLinkDialog({ open: false, item: null, type: null })}>{t("common.close")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Collega Allocazione a Rifornimenti */}
      <Dialog open={collegaDialog.open} onOpenChange={(open) => { setCollegaDialog({ ...collegaDialog, open }); setSelectedRifornimenti([]); setSelectedPerSotto({}); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{t("fuel.linkToRefueling")}</DialogTitle>
            <DialogDescription>
              Collega ogni quota dell&apos;allocazione a uno o più rifornimenti. Ogni quota va collegata separatamente.
            </DialogDescription>
          </DialogHeader>
          
          {/* Intestazione documento sorgente */}
          {collegaDialog.allocazione && (
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-2">
              {collegaDialog.allocazione.source_type === 'fattura' ? (
                <>
                  <p className="text-sm font-medium text-purple-800">
                    Fattura: {collegaDialog.allocazione.fattura_numero} del {formatDate(collegaDialog.allocazione.fattura_data)}
                  </p>
                  <p className="text-xs text-purple-700">{collegaDialog.allocazione.fornitore}</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground block">Tot. Fattura</span>
                      <span className="font-medium">{formatCurrency(collegaDialog.allocazione.fattura_totale_display || Math.abs(collegaDialog.allocazione.fattura_totale))}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Quote in attesa</span>
                      <span className="font-medium text-purple-600">{(collegaDialog.allocazione.sotto_allocazioni || []).length}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Costo Bilancio (in attesa)</span>
                      <span className="font-medium text-purple-600">{formatCurrency(collegaDialog.allocazione.costo_bilancio_totale ?? collegaDialog.allocazione.costo_bilancio)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-purple-800">
                    {collegaDialog.allocazione.source_type === 'movimento' ? 'Movimento bancario' : 'Movimento carta'}: {collegaDialog.allocazione.mov_descrizione || ''}
                  </p>
                  <p className="text-xs text-purple-700">
                    Data: {formatDate(collegaDialog.allocazione.mov_data)} · Quote in attesa: {(collegaDialog.allocazione.sotto_allocazioni || []).length}
                    · Importo in attesa: {formatCurrency(Math.abs(collegaDialog.allocazione.importo_totale ?? collegaDialog.allocazione.importo ?? 0))}
                  </p>
                </div>
              )}
              {/* Badge tipo documento */}
              {collegaDialog.allocazione.source_type === 'fattura' && collegaDialog.allocazione.tipo_documento && (
                <Badge variant="outline" className={`text-xs ${getTipoDocInfo(collegaDialog.allocazione.tipo_documento).color}`}>
                  {getTipoDocInfo(collegaDialog.allocazione.tipo_documento).label}
                </Badge>
              )}
            </div>
          )}

          {/* Sezione NCA rimossa - l'associazione ora avviene nell'Elenco Fatture (dialog Allocazioni) */}


          {/* Search rifornimenti (condiviso fra le quote) */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Cerca rifornimento per veicolo, targa, importo, stazione..." 
              value={collegaSearch}
              onChange={(e) => setCollegaSearch(e.target.value)}
              className="pl-9 h-9"
              data-testid="collega-search"
            />
          </div>

          {/* Elenco sotto-allocazioni: una sezione per ogni quota da collegare */}
          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {(collegaDialog.allocazione?.sotto_allocazioni || []).map((sotto, idx) => {
              const selPerSotto = selectedPerSotto[sotto.id] || [];
              const totSel = selPerSotto.reduce((s, id) => s + (rifNonCollegati.find(r => r.id === id)?.importo || 0), 0);
              const importoQuota = Math.abs(sotto.importo_ivato ?? sotto.importo ?? 0);
              const importoImponibile = Math.abs(sotto.importo_imponibile ?? sotto.importo ?? 0);
              const supera = collegaDialog.allocazione?.source_type === 'fattura' && totSel > importoQuota + 0.01;
              const matchPerfetto = collegaDialog.allocazione?.source_type === 'fattura' && Math.abs(totSel - importoQuota) < 0.01;
              const q = normalizeQuery(collegaSearch);
              const filteredRif = q ? rifNonCollegati.filter(r => {
                const hay = [
                  r.marca, r.modello, r.targa, r.stazione, r.note, r.data_rifornimento,
                  expandNumeric(r.importo), expandNumeric(r.litri), expandNumeric(r.prezzo_litro)
                ].filter(Boolean).join(' ').toLowerCase();
                return hay.includes(q);
              }) : rifNonCollegati;
              return (
                <div key={sotto.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`sotto-alloc-${sotto.id}`}>
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-purple-50 border-purple-300 text-purple-700">Quota {idx + 1}</Badge>
                      <span className="text-sm font-medium">Importo: {formatCurrency(importoQuota)}</span>
                      {collegaDialog.allocazione?.source_type === 'fattura' && (
                        <span className="text-xs text-muted-foreground">
                          (imponibile: {formatCurrency(importoImponibile)}
                          {sotto.costo_bilancio !== undefined && Math.abs(sotto.costo_bilancio - importoImponibile) > 0.01 && (
                            <> · costo bilancio: {formatCurrency(sotto.costo_bilancio)}</>
                          )})
                        </span>
                      )}
                      {sotto.descrizione && (
                        <span className="text-xs text-muted-foreground truncate max-w-[200px]" title={sotto.descrizione}>· {sotto.descrizione}</span>
                      )}
                    </div>
                    <Button 
                      size="sm"
                      onClick={() => handleCollegaSotto(sotto)} 
                      disabled={selPerSotto.length === 0 || supera}
                      data-testid={`collega-sotto-btn-${sotto.id}`}
                    >
                      <ArrowRight className="h-4 w-4 mr-1" /> Collega questa quota ({selPerSotto.length})
                    </Button>
                  </div>
                  {/* Totale selezionato per la quota */}
                  {selPerSotto.length > 0 && (
                    <div className={`mb-2 text-xs px-2 py-1 rounded ${
                      supera ? 'bg-red-50 text-red-700 border border-red-200' :
                      matchPerfetto ? 'bg-green-50 text-green-700 border border-green-200' :
                      'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                      Selezionati {selPerSotto.length} rifornimenti: <strong>{formatCurrency(totSel)}</strong>
                      {supera && ' — supera l\'importo della quota!'}
                      {matchPerfetto && ' — corrisponde all\'importo della quota'}
                    </div>
                  )}
                  {/* Lista rifornimenti selezionabili */}
                  <div className="max-h-44 overflow-y-auto space-y-1 border-t pt-2">
                    {filteredRif.length === 0 ? (
                      <p className="text-center text-muted-foreground py-2 text-xs">
                        {q ? 'Nessun rifornimento trovato' : 'Nessun rifornimento non collegato disponibile'}
                      </p>
                    ) : (
                      // Ordina rifornimenti suggeriti in alto (match per importo e data ravvicinata)
                      [...filteredRif].sort((ra, rb) => {
                        const refImp = importoQuota;
                        const refData = collegaDialog.allocazione?.fattura_data || collegaDialog.allocazione?.mov_data;
                        const score = (r) => {
                          if (!refImp || refImp <= 0) return 0;
                          const diff = Math.abs((Math.abs(r.importo || 0)) - refImp);
                          if (diff > 0.10) return 0;
                          let giorni = 999;
                          if (refData && r.data_rifornimento) {
                            const d1 = new Date(refData), d2 = new Date(r.data_rifornimento);
                            if (!isNaN(d1) && !isNaN(d2)) giorni = Math.abs((d1 - d2) / 86400000);
                          }
                          if (giorni > 7) return 0;
                          return (1 - diff / 0.10) * 70 + (1 - giorni / 7) * 30;
                        };
                        return score(rb) - score(ra);
                      }).map(r => {
                        const refImp = importoQuota;
                        const refData = collegaDialog.allocazione?.fattura_data || collegaDialog.allocazione?.mov_data;
                        let isSuggested = false;
                        if (refImp > 0) {
                          const diff = Math.abs((Math.abs(r.importo || 0)) - refImp);
                          if (diff <= 0.10) {
                            let giorni = 999;
                            if (refData && r.data_rifornimento) {
                              const d1 = new Date(refData), d2 = new Date(r.data_rifornimento);
                              if (!isNaN(d1) && !isNaN(d2)) giorni = Math.abs((d1 - d2) / 86400000);
                            }
                            if (giorni <= 7) isSuggested = true;
                          }
                        }
                        return (
                        <label key={r.id} className={`flex items-center gap-3 p-1.5 rounded cursor-pointer ${isSuggested ? 'bg-amber-50 border border-amber-300 hover:bg-amber-100' : 'hover:bg-muted/40'}`} data-testid={isSuggested ? `suggested-rif-${r.id}` : undefined}>
                          <Checkbox
                            checked={selPerSotto.includes(r.id)}
                            onCheckedChange={(checked) => {
                              setSelectedPerSotto(prev => {
                                const cur = prev[sotto.id] || [];
                                return {
                                  ...prev,
                                  [sotto.id]: checked ? [...cur, r.id] : cur.filter(id => id !== r.id)
                                };
                              });
                            }}
                          />
                          <div className="flex-1 text-xs">
                            <p className="font-medium flex items-center gap-1.5">
                              {isSuggested && <span title="Possibile match" className="text-amber-600">💡</span>}
                              {r.marca} {r.modello} ({r.targa}) - {formatDate(r.data_rifornimento)}
                            </p>
                            <p className="text-muted-foreground">{r.stazione || ''} {r.litri ? `| ${r.litri}L` : ''} | {formatCurrency(r.importo)}</p>
                          </div>
                        </label>
                      );})
                    )}
                  </div>
                </div>
              );
            })}
            {(collegaDialog.allocazione?.sotto_allocazioni || []).length === 0 && (
              <p className="text-center text-muted-foreground py-6">Nessuna quota da collegare</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCollegaDialog({ open: false, allocazione: null }); setSelectedPerSotto({}); }}>
              {t("common.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
